import { SelectChangeEvent } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import saveAs from "file-saver";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useCancelBulkProcess } from "../../api/services/bulkCreateProgram/bulkCreateProgramApis";
import { CancelProcessParams } from "../../api/services/bulkCreateProgram/models";
import { useFileDownloadMutation } from "../../api/services/client/clientApis";
import { useGetBulkImportQuery } from "../../api/services/manageBulkProcess/manageBulkProcess";
import { BulkImportProcessDetail } from "../../api/services/manageBulkProcess/model";
import {
  InformationBox,
  ITabs,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTabPanel,
  MuiTabs,
  MuiTextFieldWithSearchIcon,
  MuiTypography,
  OptionsDataType,
  resolveContent,
} from "../../components";
import ItemDetailRow from "../../components/itemDetailRow/ItemDetailRow";
import { LoadMoreList } from "../../components/loadMoreList/LoadMoreList";
import { STATUS_BADGE_TYPE } from "../../components/statusBadge/StatusBadge";
import {
  gridSize,
  PAGE_IDS,
  PAGE_URLS,
  SummaryStatus,
} from "../../helpers/constants";
import { getDateFormatByCountry } from "../../helpers/constants/countryDateFormat";
import { BulkProcessCategoryEnum, Category } from "../../helpers/types/common";
import {
  convertToUserFormat,
  extractFileName,
  getParsedErrorMessage,
  getProcessCategoryEnum,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
  toCamelCaseFromTitle,
} from "../../helpers/utils";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { setSelectedBulkProcess } from "../../redux/slices/manageBulkProcess/manageBulkProcessSlice";
import { SelectedBulkProcessDetails } from "../../redux/slices/manageBulkProcess/model";
import { bulkDocumentType } from "../bulkCreateProgram/modelsAndSchema/Models";
import { CancelFieldModal } from "../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import { ManageBulkProcessWrapper } from "./ManageBulkProcess.style";
import {
  headerComponentSiteCoreKeys,
  listComponentSiteCoreKeys,
  manageBulkProcessSiteCoreKeys,
  progressStateComponentSiteCoreKeys,
} from "./siteCore/SiteCoreKeys";

const TabCountBadge = ({ count }: { count: number }) => {
  const paddedCount = String(count).padStart(2, "0");
  return <MuiBox>{paddedCount}</MuiBox>;
};

const ManageBulkProcess = () => {
  const pageId = PAGE_IDS.MANAGE_BULK_PROCESS;
  const dispatch = useDispatch();
  const { isMobile } = useMediaQueries();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    pageTitle,
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { displayToast } = useToast();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<BulkImportProcessDetail>(
    {} as BulkImportProcessDetail,
  );
  const categoryDropdownSiteCore = fetchDropdownValues(
    manageBulkProcessSiteCoreKeys.headerComponentKey,
    headerComponentSiteCoreKeys.bulkProcessCategoryList,
    true,
  );
  const ALL_BULK_PROCESSES_VALUE = fetchValueByKeyOfDropdown(
    manageBulkProcessSiteCoreKeys.headerComponentKey,
    headerComponentSiteCoreKeys.bulkProcessCategoryList,
    headerComponentSiteCoreKeys.allBulkProcesses,
  );

  const [selectedCategory, setSelectedCategory] = useState({
    label: "",
    value: "",
  } as OptionsDataType);

  useEffect(() => {
    if (
      isNotEmpty(categoryDropdownSiteCore) &&
      isEmpty(selectedCategory.value)
    ) {
      setSelectedCategory(categoryDropdownSiteCore[0]);
    }
  }, [categoryDropdownSiteCore]);

  const BULK_PROCESS_TAB_ORDER = [
    SummaryStatus.PROCESSING,
    SummaryStatus.QUEUE,
    SummaryStatus.COMPLETED,
    SummaryStatus.CANCELLED,
  ];

  const selectedTabLabel = useMemo(
    () => BULK_PROCESS_TAB_ORDER[currentTabIndex],
    [currentTabIndex],
  );

  const categoryQueryParam = useMemo(() => {
    return selectedCategory.value === ALL_BULK_PROCESSES_VALUE
      ? ""
      : toCamelCaseFromTitle(selectedCategory.value as string);
  }, [selectedCategory]);

  const { data: bulkProcessApiData, isLoading: bulkProcessApiLoading } =
    useGetBulkImportQuery(
      selectedTabLabel,
      categoryQueryParam as string,
      !!selectedCategory.value,
    );

  const {
    mutateAsync: cancelBulkProcessMutate,
    isPending: isCancelProcessPending,
    isError: isCancelProcessError,
    isSuccess: isCancelProcessSuccess,
  } = useCancelBulkProcess();

  const {
    mutate: downloadFile,
    isError: isErrorOnDownload,
    isPending: isPendingOnDownload,
    isSuccess: isSuccessOnDownload,
    data: dataOnDownload,
    error: errorOnDownload,
  } = useFileDownloadMutation();

  useEffect(() => {
    if (isSuccessOnDownload) {
      let fileName = "";
      if (dataOnDownload.contentDisposition) {
        fileName = extractFileName(dataOnDownload.contentDisposition);
      }
      saveAs(dataOnDownload.blob, `${fileName}`);
    }
  }, [isSuccessOnDownload]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          bulkProcessApiLoading ||
          isCancelProcessPending ||
          isPendingOnDownload,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [bulkProcessApiLoading, isCancelProcessPending, isPendingOnDownload]);

  useEffect(() => {
    if (isErrorOnDownload && errorOnDownload) {
      displayToast(getParsedErrorMessage(errorOnDownload), "error");
    }
    if (isCancelProcessError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isErrorOnDownload, errorOnDownload, isCancelProcessError]);

  useEffect(() => {
    if (isCancelProcessSuccess) {
      setShowCancelModal(false);
      queryClient.invalidateQueries({
        queryKey: ["getBulkImport", selectedTabLabel, categoryQueryParam],
      });
    }
  }, [
    isCancelProcessSuccess,
    queryClient,
    selectedTabLabel,
    categoryQueryParam,
  ]);

  const processDetails = useMemo(
    () => bulkProcessApiData?.processDetails ?? [],
    [bulkProcessApiData],
  );

  const summary = useMemo(
    () =>
      bulkProcessApiData?.summary ?? {
        inProgressCount: 0,
        inQueueCount: 0,
        completedCount: 0,
        cancelledCount: 0,
      },
    [bulkProcessApiData],
  );

  const showSearch =
    isNotEmpty(processDetails) &&
    (selectedTabLabel === SummaryStatus.COMPLETED ||
      selectedTabLabel === SummaryStatus.CANCELLED);

  const onItemRowDetailClick = (itemData: BulkImportProcessDetail) => {
    const selectedBulkProcessDetails: SelectedBulkProcessDetails = {
      ...itemData,
    };
    dispatch(setSelectedBulkProcess(selectedBulkProcessDetails));

    const processCategory: BulkProcessCategoryEnum = getProcessCategoryEnum(
      itemData.category,
    );
    // navigating to different pages due to multiple siteCore pages, component is same
    // No permission check needed here since the same permission is applied as the current page.
    switch (processCategory) {
      case BulkProcessCategoryEnum.EDIT:
        navigate(PAGE_URLS.BULK_EDIT_PROGRAM_DETAILS);
        break;
      case BulkProcessCategoryEnum.CREATE:
        navigate(PAGE_URLS.BULK_CREATE_PROGRAM_DETAILS);
        break;
      case BulkProcessCategoryEnum.INSERT_ACTIVITIES:
        navigate(PAGE_URLS.BULK_INSERT_ACTIVITY_DETAILS);
        break;
      case BulkProcessCategoryEnum.TRANSFER_PARTICIPANTS:
        navigate(PAGE_URLS.BULK_TRANSFER_PARTICIPANTS_DETAILS);
        break;
      default:
        navigate(PAGE_URLS.BULK_CREATE_PROGRAM_DETAILS);
        break;
    }
  };

  useEffect(() => {
    if (isEmpty(processDetails) && isNotEmpty(searchQuery)) {
      setSearchQuery("");
    }
  }, [processDetails, searchQuery]);

  const badgeTypeMap = {
    [SummaryStatus.PROCESSING]: STATUS_BADGE_TYPE.inProgress,
    [SummaryStatus.QUEUE]: STATUS_BADGE_TYPE.inQueue,
    [SummaryStatus.COMPLETED]: STATUS_BADGE_TYPE.completed,
    [SummaryStatus.CANCELLED]: STATUS_BADGE_TYPE.cancelled,
  };

  const downloadUploadedFile = (item: BulkImportProcessDetail) => {
    if (item.documentId) {
      downloadFile({
        documentId: item.documentId,
        documentType: bulkDocumentType,
      });
    }
  };

  const cancelBulkProcess = (item: BulkImportProcessDetail) => {
    setShowCancelModal(true);
    setSelectedItem(item);
  };

  const menuLabels = useMemo(() => {
    return {
      downloadUploadedFile: fetchLabel(
        manageBulkProcessSiteCoreKeys.bulkProcessListKey,
        listComponentSiteCoreKeys.downloadUploadedFileOption,
      ),
      cancelBulkProcess: fetchLabel(
        manageBulkProcessSiteCoreKeys.bulkProcessListKey,
        listComponentSiteCoreKeys.cancelBulkProcessOption,
      ),
    };
  }, [fetchLabel]);

  const createMenuItems = useCallback(
    (item: BulkImportProcessDetail) => {
      const commonMenuItems = [
        {
          onClick: () => downloadUploadedFile(item),
          dataTestId: `${item.bulkProgramImportId}-menu-item-1`,
          label: menuLabels.downloadUploadedFile,
        },
      ];

      const inQueueExtraItems = [
        {
          onClick: () => cancelBulkProcess(item),
          dataTestId: `${item.bulkProgramImportId}-menu-item-2`,
          label: menuLabels.cancelBulkProcess,
        },
      ];

      return selectedTabLabel === SummaryStatus.QUEUE
        ? [...commonMenuItems, ...inQueueExtraItems]
        : commonMenuItems;
    },
    [selectedTabLabel, menuLabels],
  );

  const filteredItems = useMemo(() => {
    return !searchQuery
      ? processDetails
      : processDetails.filter((item) =>
          item?.processName?.toLowerCase().includes(searchQuery.toLowerCase()),
        );
  }, [processDetails, searchQuery]);

  const badgeType = useMemo(
    () => badgeTypeMap[selectedTabLabel],
    [selectedTabLabel],
  );

  const fetchLabelFromGivenKey = (label: string) => {
    return fetchLabel(
      manageBulkProcessSiteCoreKeys.progressStateComponent,
      label,
    );
  };

  const shouldCreateActionItems = (category: Category) => {
    const processCategory: BulkProcessCategoryEnum =
      getProcessCategoryEnum(category);
    return (
      processCategory &&
      processCategory !== BulkProcessCategoryEnum.INSERT_ACTIVITIES &&
      processCategory !== BulkProcessCategoryEnum.TRANSFER_PARTICIPANTS
    );
  };

  const renderItems = useCallback(() => {
    const showItemNavigationIcon = selectedTabLabel !== SummaryStatus.CANCELLED;

    const renderItem = (item: BulkImportProcessDetail) => (
      <React.Fragment key={item.bulkProgramImportId}>
        <ItemDetailRow
          name={item.processName}
          date={convertToUserFormat(item.processDate, userCountryDateFormat)}
          status={badgeType}
          statusLabel={fetchLabelFromGivenKey(item.status)}
          category={item?.category}
          menuItems={
            shouldCreateActionItems(item?.category)
              ? createMenuItems(item)
              : undefined
          }
          showItemNavigationIcon={showItemNavigationIcon}
          onItemRowDetailClick={() =>
            showItemNavigationIcon ? onItemRowDetailClick(item) : null
          }
        />
        <MuiBox className="dashed-bottom-border" />
      </React.Fragment>
    );

    const shouldPaginate =
      selectedTabLabel === SummaryStatus.COMPLETED ||
      selectedTabLabel === SummaryStatus.CANCELLED;

    if (shouldPaginate) {
      return <LoadMoreList items={filteredItems} renderItem={renderItem} />;
    }

    return <>{filteredItems.map(renderItem)}</>;
  }, [filteredItems, badgeType, selectedTabLabel, createMenuItems]);

  const renderTabContent = useMemo(() => {
    if (filteredItems.length > 0) {
      return renderItems();
    }

    if (searchQuery.trim()) {
      return (
        <MuiBox className="no-matching-process">
          <MuiTypography variant="h3">{commonLabels.noResult}</MuiTypography>
        </MuiBox>
      );
    }

    const getEmptyMessage = (selectedTabLabel: string) => {
      if (selectedTabLabel === SummaryStatus.COMPLETED) {
        return fetchLabelFromGivenKey(
          progressStateComponentSiteCoreKeys.noItemsCompletedMessage,
        );
      } else if (selectedTabLabel === SummaryStatus.CANCELLED) {
        return fetchLabelFromGivenKey(
          progressStateComponentSiteCoreKeys.noItemsCancelledMessage,
        );
      } else if (selectedTabLabel === SummaryStatus.PROCESSING) {
        return fetchLabelFromGivenKey(
          progressStateComponentSiteCoreKeys.NoItemsInProgressMessage,
        );
      } else if (selectedTabLabel === SummaryStatus.QUEUE) {
        return fetchLabelFromGivenKey(
          progressStateComponentSiteCoreKeys.noItemsInQueueMessage,
        );
      }
    };

    return (
      <MuiTypography className="no-matching-process" variant="h3">
        {getEmptyMessage(selectedTabLabel)}
      </MuiTypography>
    );
  }, [filteredItems, renderItems, searchQuery]);

  const tabIdInProgress = `${pageId}-tab1`;
  const tabIdInQueue = `${pageId}-tab2`;
  const tabIdCompleted = `${pageId}-tab3`;
  const tabIdCanceled = `${pageId}-tab4`;

  const MANAGE_BULK_PROCESS_TABS: ITabs[] = [
    {
      id: tabIdInProgress,
      label: fetchLabel(
        manageBulkProcessSiteCoreKeys.progressStateComponent,
        progressStateComponentSiteCoreKeys.inProgressState,
      ),
      showBadgeIcon: <TabCountBadge count={summary.inProgressCount} />,
      content: () => renderTabContent,
    },
    {
      id: tabIdInQueue,
      label: fetchLabel(
        manageBulkProcessSiteCoreKeys.progressStateComponent,
        progressStateComponentSiteCoreKeys.inQueueState,
      ),
      showBadgeIcon: <TabCountBadge count={summary.inQueueCount} />,
      content: () => renderTabContent,
    },
    {
      id: tabIdCompleted,
      label: fetchLabel(
        manageBulkProcessSiteCoreKeys.progressStateComponent,
        progressStateComponentSiteCoreKeys.completedState,
      ),
      showBadgeIcon: <TabCountBadge count={summary.completedCount} />,
      content: () => renderTabContent,
    },
    {
      id: tabIdCanceled,
      label: fetchLabel(
        manageBulkProcessSiteCoreKeys.progressStateComponent,
        progressStateComponentSiteCoreKeys.cancelledState,
      ),
      showBadgeIcon: <TabCountBadge count={summary.cancelledCount} />,
      content: () => renderTabContent,
    },
  ];

  const currentTabContent = resolveContent(
    MANAGE_BULK_PROCESS_TABS[currentTabIndex]?.content,
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTabIndex(newValue);
    setSearchQuery("");
  };

  const handleSearchQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [],
  );

  const onCategoryChange = (e: SelectChangeEvent<any>) => {
    const selectedOption = categoryDropdownSiteCore?.find(
      (option) => option.value === e.target.value,
    );
    setSelectedCategory(selectedOption as OptionsDataType);
  };

  const okButtonHandler = () => {
    if (isEmpty(selectedItem?.bulkProgramImportId)) return;
    const params: CancelProcessParams = {
      action: progressStateComponentSiteCoreKeys.cancelledState,
      bulkImportId: selectedItem.bulkProgramImportId,
    };
    cancelBulkProcessMutate({ params });
  };

  const handleModalClose = () => {
    setShowCancelModal(false);
    setSelectedItem({} as BulkImportProcessDetail);
  };

  return (
    <ManageBulkProcessWrapper className="manage-bulk-process-wrapper">
      <MuiGrid
        container
        justifyContent="space-between"
        alignItems="center"
        className="header"
      >
        <MuiGrid>
          <InformationBox title={pageTitle} />
        </MuiGrid>

        <MuiGrid size={gridSize} className="category-select-container">
          <MuiSelect
            label={fetchLabel(
              manageBulkProcessSiteCoreKeys.headerComponentKey,
              headerComponentSiteCoreKeys.category,
            )}
            options={categoryDropdownSiteCore}
            isHorizontalLabel={!isMobile}
            onChange={(e) => onCategoryChange(e)}
            value={getValueOrBlank(selectedCategory?.value as string)}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={""}
            className="category-select"
          />
        </MuiGrid>
      </MuiGrid>

      <MuiTabs
        tabs={MANAGE_BULK_PROCESS_TABS}
        value={currentTabIndex}
        onChange={handleTabChange}
        classNames={MANAGE_BULK_PROCESS_TABS.map(() => "custom-tab")}
        tabsTestIds={MANAGE_BULK_PROCESS_TABS.map((tab) => tab.id)}
      >
        <MuiBox className="tab-panel-wrapper">
          {showSearch && (
            <>
              <MuiGrid size={gridSize}>
                <MuiBox className="search-process">
                  <MuiTextFieldWithSearchIcon
                    placeholder={commonLabels.search}
                    showCloseIcon
                    shouldTrimSpaces={false}
                    value={searchQuery}
                    onChange={handleSearchQueryChange}
                    requiredARIALabel={""}
                    searchARIALabel={commonARIALabel.search}
                    data-testid="search"
                  />
                </MuiBox>
              </MuiGrid>
              <MuiBox className="search-bottom-divider" />
            </>
          )}

          <MuiTabPanel
            key={selectedTabLabel}
            panelId={`${pageId}-panel${currentTabIndex + 1}`}
            value={currentTabIndex}
            index={currentTabIndex}
            isPanelWithMount
            ariaLabelledById={MANAGE_BULK_PROCESS_TABS[currentTabIndex].id}
          >
            {currentTabContent}
          </MuiTabPanel>
        </MuiBox>
      </MuiTabs>

      {showCancelModal && (
        <CancelFieldModal
          title={fetchLabel(
            manageBulkProcessSiteCoreKeys.bulkProcessListKey,
            listComponentSiteCoreKeys.cancelBulkProcessOption,
          )}
          showModal={showCancelModal}
          handleModalClose={handleModalClose}
          handlePrimaryButtonClick={okButtonHandler}
          cancelMessage={fetchLabel(
            manageBulkProcessSiteCoreKeys.bulkProcessListKey,
            listComponentSiteCoreKeys.cancelBulkProcessConfirmationMessage,
          )}
        />
      )}
    </ManageBulkProcessWrapper>
  );
};

export default ManageBulkProcess;
